import { Input, HostBinding, HostListener, Directive, input, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { toObservable } from '@angular/core/rxjs-interop';

@Directive()
export class BaseFileAction {
  protected readonly translateService = inject(TranslateService);
  protected readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);

  readonly file = input<IrisDMSFileI>();
  readonly file$ = toObservable(this.file);

  readonly fileId = computed(() => this.file()?.id);
  readonly fileName = computed(() => this.file()?.name);
  readonly fileParentId = computed(() => this.file()?.parentId);
  readonly fileForms = computed(() => this.file()?.forms);
  readonly hasLinks = computed(() => this.file()?.hasLinks);

  @Input() set disabled(value: boolean) {
    this._disabled = value;
    this.setDisabled(value);
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() label: string;

  @Input() labelOnly = false;
  @Input() iconOnly = true;
  @Input() cssClass = '';
  @Input() faIconSizeClass = '';
  @Input() stopPropagation = false;

  @HostBinding('class.mat-mdc-menu-item')
  @HostBinding('class.is-mat-mdc-menu')
  @Input() isMatMenu = false;

  @HostBinding('class.action-button')
  @Input() isFileAction = true;

  @HostBinding('class.disabled-action-button') isFileActionDisabled = false;

  @HostListener('click', ['$event'])
  clickEvent(event: MouseEvent): void {
    this._blurByMouseEvent(event);
    if (this.isMatMenu && !this.stopPropagation) { return; }
    event.stopPropagation();
  }

  @HostListener('dblClick', ['$event'])
  dblClickEvent(event: MouseEvent): void {
    if (this.isMatMenu && !this.stopPropagation) { return; }
    event.stopPropagation();
  }

  get title(): string {
    return this.translateService.instant(this.label);
  }

  readonly isFolderLocked = this.dmsFoldersSandbox.isSelectedFolderLocked;
  readonly isFileDisabled = computed(() => {
    const disabled = this.isFolderLocked() || this.disabled || !this.file() ||  !!this.file()?.isLocked;
    this.setDisabled(disabled);
    return disabled;
  });

  protected _disabled = false;

  private _blurByMouseEvent(event: MouseEvent): void {
    const eventPath = event.composedPath();
    const activeEl = document.activeElement as HTMLElement;
    if (eventPath.includes(activeEl)) {
      activeEl.blur();
    }
  }

  public setDisabled(isDisabled: boolean): void {
    this.isFileActionDisabled = isDisabled;
    this.stopPropagation = isDisabled;
  }
}
